import React from 'react'
import ProfessionalChartLoader from '../../../common/components/molecule/Chart/ChartLoaderView/ProfessionalChartLoader'
import ChartErrorView from '../../../common/components/molecule/Chart/ChartLoaderView/ChartErrorView'
import { EChartsPresenter } from '../../../common/components/molecule/EChartsView'
import { EChartsData } from '../../../common/components/molecule/EChartsView/EChartsView'
import { PieClassKey, PieSlice } from '../types/joulyDailyTypes'

type Props = {
  data: PieSlice[]
  loading?: boolean
  error?: Error | null
}

const TITLE = 'Classes de consommation journalière'

/** Fixed display order for the 6 classes */
const CLASS_ORDER: PieClassKey[] = [
  'C1_zero',
  'C2_0_to_035',
  'C3_035_to_07',
  'C4_07_to_1',
  'C5_1_to_15',
  'C6_above_15',
]

/** Human-readable French labels per class */
const CLASS_LABELS: Record<PieClassKey, string> = {
  C1_zero:      '0 kWh',
  C2_0_to_035:  '0–0.35 kWh',
  C3_035_to_07: '0.35–0.70 kWh',
  C4_07_to_1:   '0.70–1.0 kWh',
  C5_1_to_15:   '1.0–1.5 kWh',
  C6_above_15:  '> 1.5 kWh',
}

/** Fixed 6-color palette for the pie slices */
const CLASS_COLORS: Record<PieClassKey, string> = {
  C1_zero:      '#9ca3af',
  C2_0_to_035:  '#60a5fa',
  C3_035_to_07: '#34d399',
  C4_07_to_1:   '#fbbf24',
  C5_1_to_15:   '#f97316',
  C6_above_15:  '#ef4444',
}

export default function PieClassPresenter({ data, loading, error }: Props) {
  if (loading) {
    return <ProfessionalChartLoader metricName={TITLE} />
  }

  if (error) {
    return <ChartErrorView metricName={TITLE} error={error} />
  }

  if (!data || data.length === 0) {
    return (
      <EChartsPresenter
        data={[]}
        title={TITLE}
        type="pie"
        height={400}
      />
    )
  }

  // Index slices by class key for O(1) lookup
  const byClass = new Map<PieClassKey, number>()
  for (const slice of data) {
    byClass.set(slice.class, slice.count)
  }

  // Emit all 6 classes in fixed order; omit classes with count 0
  const chartData: EChartsData[] = CLASS_ORDER
    .map((key) => ({
      name: CLASS_LABELS[key],
      value: byClass.get(key) ?? 0,
      color: CLASS_COLORS[key],
    }))
    .filter((d) => d.value > 0)

  return (
    <EChartsPresenter
      data={chartData}
      title={TITLE}
      type="pie"
      height={400}
      showLegend={true}
      showTooltip={true}
    />
  )
}
